import {BaseModel} from '@app/modules/shared/models/_base-model';

export type IncidentLabel = 'critical' | 'recoverable' | 'non-recoverable' | 'disaster' | 'data-lost';
export type IncidentSeverity = 'critical' | 'major' | 'minor';
export type IncidentStatus = 'draft' | 'published';
export type IncidentShareExpiry = 'day' | 'week' | 'month' | 'year' | 'never';

export interface IncidentTimelineEntry {
  at: string;
  note: string;
  /** `ai` rows were read out of the author's notes by the draft generator, and need review. */
  source?: 'manual' | 'uptimerobot' | 'ai';
  monitor?: string;
  kind?: 'up' | 'down';
  duration_s?: number;
}

export interface IncidentLiveState {
  is_live: boolean;
  last_update_at: string | null;
  resolution_due_at: string | null;
}

export class Incident extends BaseModel {
  id: number;
  title: string;
  occurred_at: string;
  resolved_at: string | null;
  display_timezone: string | null;
  /** Hours from occurred_at by which resolution is expected; drives the live countdown. */
  expected_time_to_resolution: number | null;
  labels: IncidentLabel[];
  severity: IncidentSeverity | null;
  status: IncidentStatus;
  introduction: string | null;
  brief_explanation: string | null;
  side_effects: string | null;
  widespread: string | null;
  security_effect: string | null;
  actual_cause: string | null;
  history: string | null;
  containment_actions: string | null;
  resolving_actions: string | null;
  further_actions: string | null;
  prevention_plan: string | null;
  timeline: IncidentTimelineEntry[];
  report_url: string;
  share_enabled: boolean;
  share_token: string | null;
  share_url: string | null;
  /** Same report through the CDN pull zone; survives the origin being down. Null with no CDN. */
  cdn_share_url: string | null;
  share_expires_at: string | null;
  share_created_at: string | null;
  share_view_count: number;
  share_last_viewed_at: string | null;
  /** Server-computed; not a column. */
  live: IncidentLiveState;
}

export interface UptimeMonitor {
  id: string;
  name: string;
  url: string;
  status: number;
}

export const INCIDENT_LABEL_OPTIONS: { value: IncidentLabel; label: string }[] = [
  {value: 'critical', label: 'Critical'},
  {value: 'recoverable', label: 'Recoverable'},
  {value: 'non-recoverable', label: 'Non-Recoverable'},
  {value: 'disaster', label: 'Disaster'},
  {value: 'data-lost', label: 'Data Lost'},
];

export const INCIDENT_SEVERITY_OPTIONS: { value: IncidentSeverity; label: string }[] = [
  {value: 'critical', label: 'Critical'},
  {value: 'major', label: 'Major'},
  {value: 'minor', label: 'Minor'},
];
